import React, { useRef, useState, MutableRefObject, useMemo, useCallback } from 'react';
import classNames from 'classnames/bind';
import Draggable, { DraggableEventHandler } from 'react-draggable';

import { NON_PREMIUM_FORECAST_DAYS } from 'common/constants';
import { useMaxWidthTablet } from 'hooks/useMediaQueries';
import type { SurfDataPoints } from 'types/surf';
import type { SurfHeight } from 'types/units';
import type { DaySelectorDay as DaySelectorDayProps, DaySelectorDays } from 'types/daySelector';
import type { SubregionDaySummaryData } from 'types/subregion';
import { GraphsCTADaySelector } from '../../../CTAs/CTAs';
import DaySelectorGraph from '../DaySelectorGraph';
import DaySelectorDay from '../DaySelectorDay';
import DayPlaceholder from '../DayPlaceholder';

import styles from './DaySelectorContent.module.scss';

export interface Props {
  activeRef?: MutableRefObject<HTMLButtonElement | null>;
  currentPosition?: { xRate: number; yRate: number };
  days: DaySelectorDays | null;
  daysAllowed: number;
  daysPerRange?: number;
  daySummary: SubregionDaySummaryData;
  daysToDisplay?: Array<number>;
  graphHeight: number;
  isHistoricView: boolean;
  onDragDaySelectorStop?: DraggableEventHandler;
  paywall?: {
    isActive?: boolean;
    segmentProperties: {
      category: string;
      pageName: string;
      spotId: string;
      subregionId: string;
      location: string;
    };
  };
  setDaysToDisplay: (startDayIndex: number) => void;
  spotId: string;
  surfData: SurfDataPoints | null;
  units: SurfHeight;
  chartIdPrefix?: string;
  isTableViewMode: boolean;
}

const cx = classNames.bind(styles);

const DaySelectorContent = ({
  activeRef,
  currentPosition,
  days,
  daysAllowed,
  daysPerRange = 0,
  daySummary,
  daysToDisplay = [],
  graphHeight,
  isHistoricView,
  onDragDaySelectorStop,
  paywall,
  setDaysToDisplay,
  spotId,
  surfData,
  units,
  chartIdPrefix,
  isTableViewMode,
}: Props) => {
  const idPrefix = chartIdPrefix ? `${chartIdPrefix}-` : '';
  const DAY_WIDTH = 75;
  const contentRef = useRef(null);
  const [hoveredDays, setHoveredDays] = useState<number[] | []>([]);
  const [focusedDay, setFocusedDay] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const resetHoverState = () => setHoveredDays([]);
  const isMaxWidthTablet = useMaxWidthTablet();
  const showDaySelectorChart = surfData && contentRef;
  const handleHover = useCallback(
    (dayIndex: number) => {
      if (!isDragging && !isTableViewMode) {
        let nextHoveredDays: number[] | [] = [];
        if (dayIndex > daysToDisplay[daysToDisplay.length - 1]) {
          nextHoveredDays = Array.from({ length: daysPerRange }, (_, index) => dayIndex - index);
        } else if (dayIndex < daysToDisplay[0]) {
          nextHoveredDays = Array.from({ length: daysPerRange }, (_, index) => dayIndex + index);
        }
        setHoveredDays(nextHoveredDays);
      } else if (!isDragging && isTableViewMode) {
        setHoveredDays([dayIndex]);
      }
    },
    [isDragging, isTableViewMode, daysToDisplay, daysPerRange],
  );

  const handleSetFocusedDay = useCallback(
    (index: number) => {
      setFocusedDay(index);
    },
    [setFocusedDay],
  );

  const handleSetDayClick = useCallback(
    (index: number) => {
      setDaysToDisplay(index);
    },
    [setDaysToDisplay],
  );

  const handleBlur = useCallback(() => {
    setFocusedDay(null);
  }, [setFocusedDay]);

  const handleDragStart: DraggableEventHandler = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (e, data) => {
      if (!isDragging) setIsDragging(true);
    },
    [isDragging, setIsDragging],
  );

  const handleDragStop: DraggableEventHandler = useCallback(
    (e, data) => {
      if (isDragging) setIsDragging(false);
      if (onDragDaySelectorStop) onDragDaySelectorStop(e, data);
    },
    [isDragging, onDragDaySelectorStop, setIsDragging],
  );

  const isPaywallVisible = useMemo(
    () => paywall?.isActive && isMaxWidthTablet,
    [isMaxWidthTablet, paywall],
  );

  const showDesktopPaywall = useMemo(
    () => paywall?.isActive && !isMaxWidthTablet,
    [isMaxWidthTablet, paywall],
  );

  const adjustedDaysAllowed = 4 + daysAllowed - 2;
  const isDraggableDisplayed = useMemo(
    () => !isPaywallVisible || (isPaywallVisible && daysToDisplay?.[0] < adjustedDaysAllowed),
    [adjustedDaysAllowed, daysToDisplay, isPaywallVisible],
  );

  const showPaywall = isPaywallVisible || showDesktopPaywall;
  let draggableLimit = 16;
  if (showPaywall) {
    draggableLimit = NON_PREMIUM_FORECAST_DAYS;
  }

  const draggableBounds = useMemo(
    () => ({
      left: 0,
      right: (draggableLimit - daysPerRange) * DAY_WIDTH,
      top: 0,
      bottom: 0,
    }),
    [daysPerRange, draggableLimit],
  );

  const draggablePosition = useMemo(
    () => ({
      x: currentPosition?.xRate || 0,
      y: currentPosition?.yRate || 0,
    }),
    [currentPosition?.xRate, currentPosition?.yRate],
  );

  return (
    <div
      className={styles.spotWrapper}
      id={`${idPrefix}day-selector-chart-${spotId}`}
      data-testid="spot-forecast-day-selector-graph"
    >
      <div
        className={styles.content}
        onMouseLeave={resetHoverState}
        onScroll={resetHoverState}
        ref={contentRef}
      >
        {showDaySelectorChart && (
          <DaySelectorGraph
            chartIdPrefix={chartIdPrefix}
            data={surfData}
            height={graphHeight}
            isPaywallVisible={isPaywallVisible}
            parentRef={contentRef}
            spotId={spotId}
            units={units}
          />
        )}
        {!isTableViewMode && (
          <Draggable
            bounds={draggableBounds}
            grid={[DAY_WIDTH, DAY_WIDTH]}
            position={draggablePosition}
            onStart={handleDragStart}
            onStop={handleDragStop}
          >
            <div
              onMouseOver={resetHoverState}
              onFocus={() => null}
              id="day-selector-graph-range-selector"
              data-testid="day-selector-graph-range-selector"
              className={styles.rangeSelector}
              style={{
                width: daysToDisplay.length * DAY_WIDTH,
                height: `${graphHeight}px`,
                display: isDraggableDisplayed ? 'block' : 'none',
              }}
            />
          </Draggable>
        )}
        <div
          className={cx('dayRanges', {
            'dayRanges--paywall_v3': isPaywallVisible,
          })}
          style={{ height: `${graphHeight}px` }}
          data-testid="day-selector-graph-dayRange"
        >
          {days?.map((dayData: DaySelectorDayProps, dayIndex: number) => {
            const isSelected = daysToDisplay.some((index) => index === dayIndex);
            const isFocused = dayIndex === focusedDay && !isSelected && isTableViewMode;
            const isHovered =
              hoveredDays.some((index) => index === dayIndex) && !isSelected && isTableViewMode;

            return dayData.surf ? (
              <DaySelectorDay
                activeRef={activeRef}
                isHovered={isHovered}
                isSelected={isSelected}
                isHistoricView={isHistoricView}
                isFocused={isFocused}
                data={dayData}
                dayIndex={dayIndex}
                daySummary={daySummary?.days?.[dayIndex]}
                handleBlur={handleBlur}
                handleFocus={handleSetFocusedDay}
                handleHover={handleHover}
                handleClick={handleSetDayClick}
                key={`${dayData.surf.max}-${dayIndex + 1}`}
                units={units}
                isTableViewMode={isTableViewMode}
              />
            ) : (
              <DayPlaceholder units={units} key={`dayPlaceholder-${dayIndex + 1}`} />
            );
          })}
        </div>
      </div>
      {showPaywall && <GraphsCTADaySelector segmentProperties={paywall?.segmentProperties} />}
    </div>
  );
};

export default DaySelectorContent;
